/*
 * Copyright (C) 2026 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import i18next from '@canvas/i18next'
import './datetimeNamespace' // registers the `en` bundle as a side effect
import {strftime} from './strftime'
import {getLocaleDateNames} from './localeDateNames'

// Per-key fallback to English -- a locale missing a format template won't
// crash.
export function getDatetimeBundle(): Record<string, unknown> {
  return {
    ...i18next.getResourceBundle('en', 'datetime'),
    ...i18next.getResourceBundle(i18next.language, 'datetime'),
  }
}

// Replacement for I18n.lookup('date.formats.*' / 'time.formats.*').
export function lookupDatetime(key: string): string | null {
  const template = getDatetimeBundle()[key.replace(/^#/, '')]
  return typeof template === 'string' ? template : null
}

const JOIN_FORMAT_DEFAULTS: Record<string, string> = {
  'time.event': '%{date} at %{time}',
  'timeValue.event': '%{dateValue} %{timeValue}',
}

// Replacement for I18n.t('#time.event' / '#timeValue.event', default, vars).
// Templates use legacy `%{name}` placeholders (Rails / i18n-js), not i18next `{{name}}`.
export function formatDatetimeJoin(
  key: 'time.event' | 'timeValue.event',
  vars: Record<string, string>,
): string {
  const template = lookupDatetime(key) ?? JOIN_FORMAT_DEFAULTS[key]
  return template.replace(/%\{(\w+)\}/g, (_match, name: string) => vars[name] ?? '')
}

// Replacement for the old @canvas/i18n I18n.l()/Scope#localize().
export function localizeDate(date: Date | null | undefined, formatKey: string): string {
  // Some upstream parsers (fudgeDateForProfileTimezone, moment-utils
  // parse()) are typed nullable; guard rather than crash in strftime().
  if (date == null) return ''

  const key = formatKey.replace(/^#/, '') // i18nliner runtime-key prefix
  const template = lookupDatetime(key)
  if (typeof template !== 'string') {
    return date.toString() // legacy i18n-js bail-out for an unresolvable key
  }

  let result = strftime(date, template, getLocaleDateNames(i18next.language))
  if (/^(date|time)/.test(key)) {
    // Legacy collapse, byte-for-byte: non-global, so only the first run of
    // 2+ whitespace chars collapses.
    result = result.replace(/\s{2,}/, ' ')
  }
  return result
}

// Raw-template variant (no key lookup, no '#'/whitespace handling) for the
// handlebars strftime helper and future raw-template consumers.
export function formatDateTemplate(date: Date, template: string): string {
  return strftime(date, template, getLocaleDateNames(i18next.language))
}
